import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { getStaffName } from '@/utils/auth';
import { gql, useMutation } from '@apollo/client';
import { Loader2, UploadIcon, X } from 'lucide-react';
import React, { useRef, useState } from 'react';
import { toast } from 'sonner';

const SIGN_CERTIFICATE_BY_CHIEF_TSD = gql`
   mutation SignCertificateByChiefTSD($id: ID!, $signature: String!) {
      signCertificateByChiefTSD(id: $id, signature: $signature) {
         id
         certificateStatus
         certificateNumber
         dateIssued
         chiefTSDSignature {
            data
            contentType
         }
      }
   }
`;

const FORWARD_CERTIFICATE = gql`
   mutation ForwardCertificate($id: ID!, $applicationType: String!) {
      forwardCertificate(id: $id, applicationType: $applicationType) {
         id
         certificateStatus
      }
   }
`;

const UPDATE_PERMIT_STAGE = gql`
   mutation UpdatePermitStage(
      $id: ID!
      $currentStage: String!
      $status: String!
      $certificateSignedByChiefTSD: Boolean!
   ) {
      updatePermitStage(
         id: $id
         currentStage: $currentStage
         status: $status
         certificateSignedByChiefTSD: $certificateSignedByChiefTSD
      ) {
         id
         currentStage
         status
         certificateSignedByChiefTSD
      }
   }
`;

const ChiefTSDSignatureModal = ({ isOpen, onClose, application, onSignComplete }) => {
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [signCertificateByChiefTSD] = useMutation(SIGN_CERTIFICATE_BY_CHIEF_TSD);
   const [forwardCertificate] = useMutation(FORWARD_CERTIFICATE);
   const [updatePermitStage] = useMutation(UPDATE_PERMIT_STAGE);
   const [signature, setSignature] = useState(null);
   const fileInputRef = useRef(null);
   const StaffName = getStaffName();

   const handleSignatureUpload = (event) => {
      const file = event.target.files[0];
      if (file) {
         const reader = new FileReader();
         reader.onload = (e) => {
            setSignature(e.target.result);
         };
         reader.readAsDataURL(file);
      }
   };

   const triggerFileInput = (e) => {
      e.preventDefault();
      e.stopPropagation();
      fileInputRef.current.click();
   };

   const handleRemoveSignature = () => {
      setSignature(null);
      if (fileInputRef.current) {
         fileInputRef.current.value = '';
      }
   };

   const handleSign = async () => {
      if (!signature) {
         toast.error('Please upload your signature first');
         return;
      }

      if (!application?.certificateId) {
         toast.error('No certificate found for this application');
         console.error('Certificate ID missing:', application);
         return;
      }

      try {
         setIsSubmitting(true);

         // 1. Sign the certificate
         const { data: signData } = await signCertificateByChiefTSD({
            variables: {
               id: application.certificateId,
               signature: signature.split(',')[1] || signature
            }
         });

         if (signData?.signCertificateByChiefTSD) {
            // 2. Forward the certificate to PENR/CENR Officer
            await forwardCertificate({
               variables: {
                  id: application.certificateId,
                  applicationType: 'Certificate of Verification'
               }
            });

            // 3. Update permit stage
            await updatePermitStage({
               variables: {
                  id: application.id,
                  currentStage: 'PendingSignatureByPENRCENROfficer',
                  status: 'In Progress',
                  certificateSignedByChiefTSD: true
               }
            });

            toast.success('Certificate signed and forwarded to PENR/CENR Officer');
            onSignComplete();
            onClose();
         }
      } catch (error) {
         console.error('Error signing certificate:', error);
         toast.error(error.message || 'Failed to sign certificate');
      } finally {
         setIsSubmitting(false);
      }
   };

   return (
      <Dialog open={isOpen} onOpenChange={onClose}>
         <DialogContent className="max-w-md">
            <DialogHeader>
               <DialogTitle>Chief TSD Signature</DialogTitle>
               <DialogDescription>
                  You are about to sign the Certificate of Verification for application {application?.applicationNumber}.
                  After signing, it will be forwarded to PENR/CENR Officer for final signature.
               </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
               <div className="border rounded-lg p-4 bg-muted/50">
                  <p className="text-sm font-medium">Certificate Details:</p>
                  <ul className="mt-2 space-y-2 text-sm">
                     <li>Application Number: {application?.applicationNumber}</li>
                     <li>Application Type: {application?.applicationType}</li>
                     <li>Status: {application?.status}</li>
                  </ul>
               </div>

               <div className="text-center">
                  <div className="relative h-32 mb-4 border-2 border-dashed rounded-md flex items-center justify-center bg-accent/50">
                     {signature ? (
                        <>
                           <img
                              src={signature}
                              alt="Chief TSD Signature"
                              className="max-w-full max-h-full object-contain"
                           />
                           <Button
                              variant="ghost"
                              size="icon"
                              className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-100 hover:bg-red-200"
                              onClick={handleRemoveSignature}
                           >
                              <X className="h-4 w-4 text-red-600" />
                           </Button>
                        </>
                     ) : (
                        <p className="text-muted-foreground">Chief TSD Signature</p>
                     )}
                  </div>
                  <p className="font-semibold text-foreground">{StaffName}</p>
                  <p className="text-xs text-muted-foreground">Chief TSD</p>
                  <input
                     type="file"
                     ref={fileInputRef}
                     className="hidden"
                     accept="image/*"
                     onChange={handleSignatureUpload}
                  />
                  <Button
                     variant="outline"
                     size="sm"
                     className="mt-2"
                     onClick={triggerFileInput}
                  >
                     <UploadIcon className="h-4 w-4 mr-2" />
                     Upload Signature
                  </Button>
               </div>
            </div>

            <DialogFooter>
               <Button
                  variant="outline"
                  onClick={onClose}
                  disabled={isSubmitting}
               >
                  Cancel
               </Button>
               <Button
                  onClick={handleSign}
                  disabled={isSubmitting || !signature}
                  className="bg-green-600 text-white hover:bg-green-700"
               >
                  {isSubmitting ? (
                     <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Signing and Forwarding...
                     </>
                  ) : (
                     'Sign and Forward to PENR/CENR Officer'
                  )}
               </Button>
            </DialogFooter>
         </DialogContent>
      </Dialog>
   );
};

export default ChiefTSDSignatureModal;